import signalRService from '@/services/signalR/SignalRService';

const LOCAL_KEY = 'TOPPION:open-tabs-local';
const POLL_MS = 8_000;
const STALE_MS = 3 * 60 * 1000;
const EVENT_NAME = 'openTabBeat';

export type OpenTabInfo = {
  tabId: string;
  title: string;
  path: string;
  browser: string;
  ip: string;
  sameMachine: boolean;
  self: boolean;
};

export type OpenTabSnapshot = {
  count: number;
  tabs: OpenTabInfo[];
};

const EMPTY_SNAPSHOT: OpenTabSnapshot = { count: 0, tabs: [] };

type TabRecord = {
  tabId: string;
  userId: string;
  machineKey: string;
  ip: string;
  title: string;
  path: string;
  browser: string;
  at: number;
};

type StartOptions = {
  userId: string;
  getMeta: () => { title: string; path: string };
};

let snapshot: OpenTabSnapshot = EMPTY_SNAPSHOT;
const listeners = new Set<() => void>();
const peers = new Map<string, TabRecord>();
let selfTabId = '';
let machineKey = '';
let selfIp = '';
let activeRoom = '';
let remoteClosed = false;
let metaReader: StartOptions['getMeta'] | null = null;
let refreshNow: (() => void) | null = null;

const createId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const hashText = (value: string) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
};

const buildMachineKey = () => {
  if (typeof window === 'undefined') return 'server';
  const platform = String(navigator.platform || '').toLowerCase();
  const os = platform.includes('win')
    ? 'windows'
    : platform.includes('mac')
      ? 'mac'
      : platform.includes('linux')
        ? 'linux'
        : platform || 'unknown';
  const screenInfo = typeof screen === 'undefined'
    ? ''
    : [screen.width, screen.height, screen.colorDepth].join('x');
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const raw = [
    os,
    screenInfo,
    navigator.hardwareConcurrency || 0,
    navigator.maxTouchPoints || 0,
    timezone,
  ].join('|');
  return hashText(raw);
};

const normalizeIp = (value: string) => {
  let ip = value.trim();
  if (ip.startsWith('::ffff:')) ip = ip.slice('::ffff:'.length);
  if (ip === '::1') return '127.0.0.1';
  return ip;
};

const isSameMachine = (tab: Pick<TabRecord, 'machineKey' | 'ip'>) => {
  const ip = normalizeIp(tab.ip || '');
  if (selfIp && ip && ip === selfIp) return true;
  return Boolean(tab.machineKey && tab.machineKey === machineKey);
};

const browserName = () => {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Safari/')) return 'Safari';
  return 'Trình duyệt';
};

const sameSnapshot = (left: OpenTabSnapshot, right: OpenTabSnapshot) => {
  if (left.count !== right.count || left.tabs.length !== right.tabs.length) return false;
  return left.tabs.every((tab, index) => {
    const other = right.tabs[index];
    return tab.tabId === other.tabId
      && tab.title === other.title
      && tab.path === other.path
      && tab.browser === other.browser
      && tab.ip === other.ip
      && tab.sameMachine === other.sameMachine
      && tab.self === other.self;
  });
};

const publishSnapshot = (next: OpenTabSnapshot) => {
  if (sameSnapshot(snapshot, next)) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const currentRecord = (userId: string): TabRecord | null => {
  if (!selfTabId || !metaReader) return null;
  const meta = metaReader();
  return {
    tabId: selfTabId,
    userId,
    machineKey,
    ip: selfIp,
    title: meta.title.trim(),
    path: meta.path.trim(),
    browser: browserName(),
    at: Date.now(),
  };
};

const readLocalTabs = (): TabRecord[] => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as TabRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalTabs = (tabs: TabRecord[]) => {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(tabs));
  } catch {
    // Trình duyệt chặn localStorage thì bỏ qua bản dự phòng cùng trình duyệt.
  }
};

const remember = (tab: TabRecord) => {
  if (!tab.tabId) return;
  const prev = peers.get(tab.tabId);
  peers.set(tab.tabId, {
    ...tab,
    ip: tab.ip || prev?.ip || '',
    machineKey: tab.machineKey || prev?.machineKey || '',
  });
};

const sweep = () => {
  const now = Date.now();
  peers.forEach((tab, tabId) => {
    if (tabId !== selfTabId && now - tab.at > STALE_MS) peers.delete(tabId);
  });
};

const publishPeers = () => {
  sweep();
  const tabs = [...peers.values()]
    .map((tab) => ({
      tabId: tab.tabId,
      title: tab.title,
      path: tab.path,
      browser: tab.browser,
      ip: normalizeIp(tab.ip || ''),
      sameMachine: isSameMachine(tab),
      self: tab.tabId === selfTabId,
    }))
    .sort((a, b) => Number(b.self) - Number(a.self) || Number(b.sameMachine) - Number(a.sameMachine) || a.title.localeCompare(b.title, 'vi'));
  publishSnapshot({ count: tabs.length, tabs });
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
};

const readString = (record: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

export const getOpenTabSnapshot = () => snapshot;

export const getOpenTabServerSnapshot = () => EMPTY_SNAPSHOT;

export const subscribeOpenTabs = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const touchOpenTab = () => {
  refreshNow?.();
};

export function requestCloseOpenTab(tabId: string) {
  if (!tabId || tabId === selfTabId || !activeRoom || !signalRService.isConnectionActive()) return;
  peers.delete(tabId);
  publishPeers();
  const self = currentRecord('');
  void signalRService.sendEventToGroup(activeRoom, EVENT_NAME, {
    tabId: self?.tabId || selfTabId,
    targetTabId: tabId,
    kind: 'close',
  }).catch(() => undefined);
}

export function startOpenTabTracking(options: StartOptions): () => void {
  if (typeof window === 'undefined' || !options.userId) return () => undefined;

  let stopped = false;
  let joined = false;
  remoteClosed = false;
  selfTabId = createId();
  machineKey = buildMachineKey();
  metaReader = options.getMeta;
  const room = `opentabs:${options.userId}`;
  activeRoom = room;
  let timer = 0;

  const rememberSelf = () => {
    const self = currentRecord(options.userId);
    if (self) remember(self);
  };

  const publishLocalFallback = () => {
    const now = Date.now();
    const next = readLocalTabs().filter((tab) => tab.userId === options.userId && now - tab.at <= STALE_MS && tab.tabId !== selfTabId);
    const self = currentRecord(options.userId);
    if (self) next.push(self);
    writeLocalTabs(next);
    next.forEach(remember);
    publishPeers();
  };

  const markClosedByOtherTab = () => {
    if (remoteClosed) return;
    remoteClosed = true;
    stopped = true;
    window.clearInterval(timer);
    peers.delete(selfTabId);
    publishPeers();
    void sendBeat('bye').catch(() => undefined);
    window.location.replace('about:blank');
  };

  const sendBeat = async (kind: 'hello' | 'beat' | 'bye' | 'close', targetTabId = '') => {
    if (remoteClosed && kind !== 'bye') return;
    const self = currentRecord(options.userId);
    if (!self || !signalRService.isConnectionActive()) return;
    if (!joined) {
      await signalRService.joinGroup(room);
      joined = true;
    }
    await signalRService.sendEventToGroup(room, EVENT_NAME, {
      ...self,
      targetTabId,
      kind,
    });
  };

  const onBeat = (raw: unknown) => {
    const record = asRecord(raw);
    if (!record) return;
    const tabId = readString(record, 'tabId', 'TabId');
    if (!tabId) return;
    const kind = readString(record, 'kind', 'Kind');
    const targetTabId = readString(record, 'targetTabId', 'TargetTabId');
    if (kind === 'close') {
      if (targetTabId === selfTabId) markClosedByOtherTab();
      return;
    }
    if (kind === 'bye') {
      if (tabId !== selfTabId) peers.delete(tabId);
      publishPeers();
      return;
    }
    const ip = normalizeIp(readString(record, 'ip', 'Ip'));
    if (tabId === selfTabId && ip) selfIp = ip;
    remember({
      tabId,
      userId: options.userId,
      machineKey: readString(record, 'machineKey', 'MachineKey'),
      ip,
      title: readString(record, 'title', 'Title'),
      path: readString(record, 'path', 'Path'),
      browser: readString(record, 'browser', 'Browser'),
      at: Date.now(),
    });
    publishPeers();
    if (kind === 'hello' && tabId !== selfTabId) {
      void sendBeat('beat').catch(() => undefined);
    }
  };

  const refresh = async () => {
    if (stopped || remoteClosed) return;
    rememberSelf();
    publishLocalFallback();
    if (!stopped && !remoteClosed) {
      void sendBeat('beat').catch(() => undefined);
    }
  };

  const onConnected = (connected: boolean) => {
    if (!connected) {
      joined = false;
      return;
    }
    void sendBeat('hello').catch(() => undefined);
  };

  const onReconnect = () => {
    joined = false;
    void sendBeat('hello').catch(() => undefined);
  };

  refreshNow = () => {
    void refresh();
  };

  signalRService.setOnDynamicEvent(EVENT_NAME, onBeat);
  signalRService.setOnConnectionStateChanged(onConnected);
  signalRService.onReconnect(onReconnect);
  if (signalRService.isConnectionActive()) void sendBeat('hello').catch(() => undefined);

  const onPageHide = () => {
    const self = currentRecord(options.userId);
    if (self) {
      const next = readLocalTabs().filter((tab) => tab.tabId !== self.tabId);
      writeLocalTabs(next);
    }
    void sendBeat('bye').catch(() => undefined);
  };

  void refresh();
  timer = window.setInterval(() => {
    void refresh();
  }, POLL_MS);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('pagehide', onPageHide);
    signalRService.removeOnDynamicEvent(EVENT_NAME, onBeat);
    signalRService.removeOnConnectionStateChanged(onConnected);
    signalRService.removeOnReconnect(onReconnect);
    if (refreshNow) refreshNow = null;
    void sendBeat('bye').catch(() => undefined);
    peers.clear();
    metaReader = null;
    activeRoom = '';
    publishSnapshot(EMPTY_SNAPSHOT);
  };
}
